import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

/**
 * 参数示例:
 * ```typescript
 * {
 *   "prompt": "A beautiful landscape with mountains and a lake at sunset",
 *   "aspect_ratio": "16:9",
 *   "duration": 5,
 *   "quality": "540p",
 *   "motion_mode": "normal",
 *   "seed": 12345
 * }
 * ```
 */
export interface PixverseV45T2vParams {
  /**
   * 正向提示词，描述要生成的视频内容，必填参数
   */
  prompt: string;
  /**
   * 视频宽高比，可选参数，默认16:9
   * @default "16:9"
   * @values 16:9 | 9:16 | 1:1 | 4:3 | 3:4
   */
  aspect_ratio?: '16:9' | '9:16' | '1:1' | '4:3' | '3:4';
  /**
   * 视频时长（秒），可选参数，支持5或10秒，默认5秒
   * @default 5
   * @values 5 | 10
   */
  duration?: 5 | 10;
  /**
   * 视频质量，可选参数，默认540p
   * @default "540p"
   * @values 270p | 540p | 720p | 1080p
   */
  quality?: '270p' | '540p' | '720p' | '1080p';
  /**
   * 负向提示词，描述不希望在视频中出现的内容，可选参数
   */
  negative_prompt?: string;
  /**
   * 运动模式，可选参数。normal（普通速度）或 fast（快速运动）
   * @values normal | fast
   */
  motion_mode?: 'normal' | 'fast';
  /**
   * 摄像机运动，可选参数。控制视频中的摄像机运动方向
   * @values zoom_in | zoom_out | pan_left | pan_right | tilt_up | tilt_down
   */
  camera_movement?: 'zoom_in' | 'zoom_out' | 'pan_left' | 'pan_right' | 'tilt_up' | 'tilt_down';
  /**
   * 随机种子，可选参数，用于生成可重复的结果
   */
  seed?: number;
  /**
   * 模板ID，可选参数，用于使用预定义模板
   */
  template_id?: number;
}

export interface PixverseV45T2vResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * pixverse_v4_5_t2v
 *
 * PixVerse V4.5 文本到视频生成接口，将文本提示词转换为视频。
 * 
 * Model: v4.5
 * Features:
 * - 文本到视频生成
 * - 支持5种宽高比
 * - 支持5或10秒时长
 * - 支持4种质量级别
 * - 支持运动模式控制
 * - 支持摄像机运动控制
 *
 * @example
 * ```typescript
 * import { initSeacloud, pixverseV45T2v } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await pixverseV45T2v(
 *   {
 *     "prompt": "A beautiful landscape with mountains and a lake at sunset",
 *     "aspect_ratio": "16:9",
 *     "duration": 5,
 *     "quality": "540p",
 *     "motion_mode": "normal",
 *     "seed": 12345
 *   }
 * );
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 PixverseV45T2vParams 接口定义
 * @returns 生成的资源列表
 */
export async function pixverseV45T2v(
  params: PixverseV45T2vParams,
): Promise<PixverseV45T2vResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 5000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'pixverse_v4_5_t2v',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: PixverseV45T2vResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
